import * as React from 'react';
import { useDrag } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';

import { Api } from '../../typings/api';
import { ListDisplayType } from '../../containers/DriveItemList/constants';
import { LoaderStep } from '../Loader';
import {
	ListItemCardInner, ListItemContainerVariants, ListItemCardVariants,
	ListItemComponentVariants, ListItemUnreadBadge
} from './styles';
import DriveItemUtils, { ThumbnailDescriptor } from '../../utils/driveItem';
import ListItemThumbnail from './Thumbnail';
import ListItemInfos from './../../containers/ListItem/Infos';
import { DndItemTypes } from '../../typings/dnd';
import DropTarget from '../../containers/DriveItemList/DropTarget';
import FolderDropTarget from '../../containers/DriveItemList/FolderDropTarget';
import { SelectionType } from '../../containers/Selection/actions';

interface ListItemProps {
	onClick: () => void;
	showUnreadBadge: boolean;
	displayType: ListDisplayType;
	itemType: Api.AssetType;
	hasActionButton: boolean;
	label: string;
	isSelectedForContextualMenu: boolean;
	isDownloading: boolean;
	isDownloaded: boolean;
	isDownloadInProgress: boolean;
	isUpdating: boolean;
	loaderStep: LoaderStep | undefined;
	thumbnailDescriptor: ThumbnailDescriptor;
	isUnread: boolean;
	isEditMode: boolean;
	itemId?: number;
	hasSelectionIcon?: boolean;
	isDraggable?: boolean;
	index?: number;
	item?: Api.DriveItem;
	itemsCount?: number;
	draggableItemsCount?: number;
	isDragPreview?: boolean;
	selectionType?: SelectionType;
	isMultipleSelection?: boolean;
	isSelectedInMultipleSelection?: boolean;
	tagClasses?: string[] | undefined;
}

const ListItem = ({
	onClick,
	displayType,
	itemType,
	hasActionButton,
	label,
	isSelectedForContextualMenu,
	isDownloading,
	isDownloadInProgress,
	isUpdating,
	loaderStep,
	thumbnailDescriptor,
	isUnread,
	isEditMode,
	itemId,
	hasSelectionIcon = true,
	isDraggable,
	index = -1,
	item,
	itemsCount = 0,
	draggableItemsCount = 0,
	isDragPreview,
	selectionType,
	isMultipleSelection,
	isSelectedInMultipleSelection,
	tagClasses,
}: ListItemProps) => {
	const [isActive, setIsActive] = React.useState(false);

	const [{ isDragging }, drag, preview] = useDrag({
		item: {
			type: DndItemTypes.DRIVE_ITEM,
			index,
			driveItem: item,
			displayType,
		},
		begin: () => {
			// using react state cause performance issues when there are many items in drive
			setTimeout(() => document.body.classList.add('is-dragging'), 0);
		},
		end: () => {
			document.body.classList.remove('is-dragging');
		},
		collect: monitor => ({
			isDragging: !!monitor.isDragging(),
		}),
	});

	React.useEffect(() => {
		preview(getEmptyImage(), { captureDraggingState: true });
	}, []);

	const handleContextualMenuHover = (e: MouseEvent, isActive: boolean) => {
		e.stopPropagation();
		setIsActive(isActive);
	};

	const onCardLeave = (e: MouseEvent) => {
		handleContextualMenuHover(e, false);
	};

	const onContextualMenuHover = (e: MouseEvent) => {
		handleContextualMenuHover(e, true);
	};

	const ListItemContainer = ListItemContainerVariants[displayType];
	const isActivated = isSelectedForContextualMenu || isActive;
	const ListItemCard = isActivated
		? ListItemCardVariants[displayType]
		: ListItemComponentVariants[displayType];

	const className = DriveItemUtils.getItemClassName('ListItemContainer', item, tagClasses);

	return (
		<ListItemContainer
			className={className}
			isEditMode={isEditMode}
			itemsCount={itemsCount}
		>
			{isDraggable &&
				<DropTarget index={index} />
			}
			{isDraggable && index === draggableItemsCount - 1 &&
				<DropTarget
					index={index + 1}
					isLast={true}
				/>
			}
			{isDraggable && itemType === Api.DriveItemType.Directory &&
				<FolderDropTarget folderId={itemId as number} />
			}
			{isUnread && displayType !== ListDisplayType.Coverflow &&
				<ListItemUnreadBadge />
			}
			<ListItemCard
				ref={isDraggable ? drag : null}
				isDraggable={isDraggable}
				isDragging={isDragging}
				isDragPreview={isDragPreview}
				onClick={isDraggable ? undefined : onClick}
				isSelectedForContextualMenu={isSelectedForContextualMenu}
				onMouseLeave={onCardLeave}
			>
				<ListItemCardInner isRowView={displayType === ListDisplayType.Row}>
					<ListItemThumbnail
						selectionId={itemId}
						displayType={displayType}
						thumbnailDescriptor={thumbnailDescriptor}
						isActivated={isActivated}
						hasSelectionIcon={hasSelectionIcon && displayType !== ListDisplayType.Coverflow}
						isMultipleSelection={isMultipleSelection && itemType !== Api.DriveItemType.Directory}
						isSelectedInMultipleSelection={isSelectedInMultipleSelection}
						selectionType={selectionType}
						isDownloading={isDownloading}
						isDownloadInProgress={isDownloadInProgress}
						isUpdating={isUpdating}
						loaderStep={loaderStep}
					/>
					{displayType !== ListDisplayType.Coverflow &&
						<ListItemInfos
							itemId={itemId}
							itemType={itemType}
							hasActionButton={hasActionButton}
							label={label}
							onContextualMenuHover={onContextualMenuHover}
							isActiveIcon={isActivated}
							isRowView={displayType === ListDisplayType.Row}
						/>
					}
				</ListItemCardInner>
			</ListItemCard>
		</ListItemContainer>
	);
};

export default ListItem;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/ListItem/index.tsx